import React, { useCallback, useEffect, useState } from "react";
import { actAsApi } from "core/api";
import {
  clearActAsSession,
  getActAsSession,
  parseActAsExpiry,
  setActAsSession,
  subscribeActAsSession,
} from "core/act-as/act-as.session";
import { ActAsSession } from "core/types/act-as.types";

type ActAsContextValue = {
  session?: ActAsSession;
  isStarting: boolean;
  isStopping: boolean;
  // `reason` non e' opzionale: il backend rifiuta l'apertura senza motivo.
  start: (subjectId: string, reason: string) => Promise<void>;
  stop: () => Promise<void>;
};

const initialState: ActAsContextValue = {
  session: undefined,
  isStarting: false,
  isStopping: false,
  start: async () => {},
  stop: async () => {},
};

const ActAsContext = React.createContext<ActAsContextValue>(initialState);

type ActAsProviderProps = {
  children: React.ReactNode;
};

function ActAsProvider({ children }: ActAsProviderProps) {
  // La sorgente di verita' e' il modulo core/act-as/act-as.session (letto
  // dall'interceptor axios): qui se ne tiene solo una copia per far ridisegnare la UI.
  const [session, setSession] = useState<ActAsSession | undefined>(
    getActAsSession()
  );
  const [isStarting, setIsStarting] = useState(false);
  const [isStopping, setIsStopping] = useState(false);

  useEffect(() => subscribeActAsSession(setSession), []);

  useEffect(() => {
    if (!session) return;
    // Senza questo timer la sessione scaduta resterebbe a schermo finche' non parte
    // una richiesta: lo stato invisibile e' il difetto numero uno di questa feature.
    const expiry = parseActAsExpiry(session.expiresAt);
    if (Number.isNaN(expiry)) return;
    const timer = setTimeout(
      () => clearActAsSession(),
      Math.max(expiry - Date.now(), 0)
    );
    return () => clearTimeout(timer);
  }, [session]);

  // Dopo apertura e chiusura si ricarica la pagina: lo store redux contiene pratiche,
  // ricerche e URL dell'ALTRA identita' e non ha un reset globale. Un reload e' il modo
  // piu' economico di garantire che a schermo non resti niente del contesto precedente.
  const reload = () => window.location.reload();

  const start = useCallback(async (subjectId: string, reason: string) => {
    setIsStarting(true);
    try {
      const newSession = await actAsApi.start(subjectId, reason);
      setActAsSession(newSession);
      reload();
    } finally {
      setIsStarting(false);
    }
  }, []);

  const stop = useCallback(async () => {
    const active = getActAsSession();
    if (!active) return;
    setIsStopping(true);
    try {
      await actAsApi.stop(active.sessionId);
    } catch (error) {
      // Anche se la chiusura lato server fallisce, l'header va comunque tolto da questo
      // browser: meglio una riga aperta che scadra' da sola che continuare a operare
      // per conto del cliente credendo di aver smesso.
      console.error(error);
    } finally {
      clearActAsSession();
      setIsStopping(false);
      reload();
    }
  }, []);

  return (
    <ActAsContext.Provider value={{ session, isStarting, isStopping, start, stop }}>
      {children}
    </ActAsContext.Provider>
  );
}

const useActAs = () => React.useContext(ActAsContext);

export { ActAsProvider, ActAsContext, useActAs };
